import React, { useEffect, useState, lazy } from 'react';
import { Clock, Sun, Zap, Leaf, ChevronLeft, ChevronRight } from 'lucide-react';
export function OptimizedSaffronSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const slides = [{
    icon: Clock,
    title: 'Boswellic Acids',
    subtitle: 'The Comfort Restorer',
    image: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/Untitled_design_29.png?v=1764266450',
    description: <>
          Supports your body's healthy inflammation response and joint tissue.
          So you move through your day with ease — not stiffness holding you
          back.
        </>,
    delay: '0.3s'
  }, {
    icon: Sun,
    title: 'AKBA',
    subtitle: 'The Mobility Compound',
    image: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/Untitled_design_27.png?v=1764266450',
    description: <>
          Targets your body's natural inflammation response at the source. So your joints stay flexible and comfortable — not stiff and restricted from daily demands.
        </>,
    delay: '0.4s'
  }, {
    icon: Zap,
    title: 'β-Boswellic Acid',
    subtitle: 'The Ease Restorer',
    image: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/Untitled_design_28.png?v=1764266450',
    description: <>
          Supports joint tissue integrity and long-term physical comfort. So your body can move freely, recover fully, and carry you through the life God called you to live.
        </>,
    delay: '0.5s'
  }];
  // Auto-advance slideshow
  useEffect(() => {
    if (!isPaused) {
      const timer = setInterval(() => {
        setActiveSlide(prev => (prev + 1) % slides.length);
      }, 5005);
      return () => clearInterval(timer);
    }
  }, [isPaused, slides.length]);
  const goToSlide = (index: number) => setActiveSlide(index);
  const nextSlide = () => setActiveSlide(prev => (prev + 1) % slides.length);
  const prevSlide = () => setActiveSlide(prev => (prev - 1 + slides.length) % slides.length);
  const ActiveIcon = slides[activeSlide].icon;
  return <>
      <style data-id="element-285">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap');
          
          .font-serif { font-family: 'Playfair Display', serif; }
          .font-sans { font-family: 'Inter', sans-serif; }
          
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fadeInUp 0.8s ease-out forwards;
          }

          @keyframes slideIn {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .slide-enter {
            animation: slideIn 0.5s ease-out forwards;
          }
        `}
      </style>

      <section style={{
      backgroundColor: '#FEFDFB'
    }} className="w-full relative overflow-hidden py-16 md:py-24 text-[#474747]" data-id="element-286">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8" data-id="element-287">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12 animate-fade-in relative z-10" data-id="element-288">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-2 tracking-tight" style={{
            color: '#496f58'
          }} data-id="element-289">
              Why F&F{' '}
              <span className="relative inline-block px-1" data-id="element-290">
                <span className="absolute bottom-1 left-0 w-full h-[55%] bg-[#ffe175] opacity-90 transform -skew-x-6 -rotate-1 rounded-sm -z-10" data-id="element-291"></span>
                <span className="italic relative z-10" data-id="element-292">Frankincense</span>
              </span>
              <br className="hidden md:block" data-id="element-293" />
              Works When Others Don't
            </h2>
          </div>

          {/* Main Grid */}
          <div className="flex flex-col lg:flex-row items-center gap-5 lg:gap-20" data-id="element-294">
            {/* Slideshow */}
            <div className="w-full lg:w-1/2 relative z-10 order-2 lg:order-1" data-id="element-295">
              <div className="relative min-h-[280px] md:min-h-[240px]" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-296">
                <div key={activeSlide} className="group flex flex-col sm:flex-row items-start sm:items-center p-6 rounded-2xl transition-all duration-300 hover:bg-[#F5F1EB] border border-transparent hover:border-[#E8DFD4] slide-enter" data-id="element-297">
                  {/* ICON BOX */}
                  <div className="flex-shrink-0 mr-6 mb-2 sm:mb-0 bg-white p-4 rounded-xl shadow-sm border border-[#F5F1EB] relative transition-all duration-300 hover:shadow-md" data-id="element-298">
                    <ActiveIcon size={32} color="#C9A962" strokeWidth={1.5} data-id="element-299" />
                  </div>

                  <div data-id="element-300">
                    {/* TITLE */}
                    <div className="flex items-baseline gap-1 mb-1" data-id="element-301">
                      <h3 className="font-serif text-2xl text-[#496f58]" data-id="element-302">
                        {slides[activeSlide].title}
                      </h3>
                    </div>

                    {/* SUBHEADER */}
                    <p className="font-sans text-xs text-[#496f58] opacity-80 -mt-1 mb-2" data-id="element-303">
                      {slides[activeSlide].subtitle}
                    </p>

                    {/* DESCRIPTION */}
                    <p className="font-sans text-gray-600 leading-relaxed text-sm md:text-base" data-id="element-304">
                      {slides[activeSlide].description}
                    </p>
                  </div>
                </div>

                {/* Arrows */}
                <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white/90 hover:bg-white p-2 rounded-full shadow-md border border-[#E8DFD4] transition-all duration-300 hover:scale-110" data-id="element-305">
                  <ChevronLeft size={20} color="#496f58" data-id="element-306" />
                </button>
                <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white/90 hover:bg-white p-2 rounded-full shadow-md border border-[#E8DFD4] transition-all duration-300 hover:scale-110" data-id="element-307">
                  <ChevronRight size={20} color="#496f58" data-id="element-308" />
                </button>
              </div>
            </div>

            {/* Right Column */}
            <div className="w-full lg:w-1/2 relative group animate-fade-in -mr-4 md:-mr-8 lg:-mr-24 xl:-mr-32 order-1 lg:order-2" style={{
            animationDelay: '0.2s'
          }} data-id="element-309">
              <div className="relative z-10" data-id="element-310">
                <picture data-id="element-311">
                  <source media="(max-width: 1000px)" srcSet="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_11_Mobile1_08925935738419044_-_Copy.png?v=1763935219" data-id="element-312" />
                  <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_11_Mobile1_08925935738419044_-_Copy.png?v=1763935219" alt="Faith & Fig Frankincense" className="w-[115%] max-w-none md:w-[110%] lg:w-[120%] h-auto object-contain transform transition-transform duration-1000 hover:scale-[1.02]" loading="lazy" data-id="element-313" />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}